import { createContext, useContext, useRef, useState } from "react";
import { SIDEBAR_MODES, SIDEBAR_SECTIONS } from "@/business-settings/constants";
import { IReferral } from "@/referrals/referral";
import type { UserAccountValues } from "@/user/user-account-form/types";

export type BusinessSettingsSidebarMode =
  (typeof SIDEBAR_MODES)[keyof typeof SIDEBAR_MODES];
export type BusinessSettingsSidebarSection =
  (typeof SIDEBAR_SECTIONS)[keyof typeof SIDEBAR_SECTIONS];

interface SidebarState {
  isOpen: boolean;
  mode: BusinessSettingsSidebarMode;
  section: BusinessSettingsSidebarSection;
  selectedItemId: string | undefined;
  payoutReferral: IReferral | null;
}

export interface BusinessSettingsSidebarContextType {
  state: SidebarState;
  updateSidebar: (updates: Partial<SidebarState>) => void;
  resetSidebar: () => void;
  accountDrafts: React.MutableRefObject<Map<string, UserAccountValues>>;
}

const initialState: SidebarState = {
  isOpen: false,
  mode: "view",
  section: "users",
  selectedItemId: undefined,
  payoutReferral: null,
};

const BusinessSettingsSidebarContext = createContext<
  BusinessSettingsSidebarContextType | undefined
>(undefined);

export const BusinessSettingsSidebarProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [state, setState] = useState<SidebarState>(initialState);
  const accountDrafts = useRef(new Map<string, UserAccountValues>());

  const updateSidebar = (updates: Partial<SidebarState>) => {
    setState((current) => ({ ...current, ...updates }));
  };

  const resetSidebar = () => {
    setState(initialState);
  };

  return (
    <BusinessSettingsSidebarContext.Provider
      value={{
        state,
        updateSidebar,
        resetSidebar,
        accountDrafts,
      }}
    >
      {children}
    </BusinessSettingsSidebarContext.Provider>
  );
};

export const useBusinessSettingsSidebar = () => {
  const context = useContext(BusinessSettingsSidebarContext);
  if (!context) {
    throw new Error(
      "useBusinessSettingsSidebar must be used within BusinessSettingsSidebarProvider",
    );
  }
  return context;
};
